import * as React from "react"
import { Link, router, useForm, usePage } from "@inertiajs/react"
import { Button, type ButtonProps } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"
// The cellar dialog dress (.studio-modal, .studio-gate-form, .studio-consent),
// shared with the studio's download gate this one is modelled on.
import "@/styles/public-studio.css"
import "@/styles/public-brochure.css"

// The brochure behind the email gate (F10, M7 decision 12).
//
// One gate per page: PublicShell mounts <BrochureGateProvider>, which owns
// the dialog and the download, and every "Download our brochure" link or
// button opens it. A guest gives a first name and email (marketing consent
// optional, unticked); a signed-in visitor skips the form. POST /brochure
// redirects back with flash.brochure_token, and the provider then starts
// GET /brochure/file?token=… with a plain navigation (the PDF answers as an
// attachment, so the page stays put). The server also emails a copy.
//
// Every brochure link hides itself when no PDF is attached
// (`brochure_available`). Gate errors arrive keyed brochure_first_name,
// brochure_email and brochure_base (the rate limit), never first_name or
// email, so they cannot collide with a page's own form (the contact form).

type GateFields = {
  first_name: string
  email: string
  marketing_consent: boolean
  website: string
}

type GateErrors = Partial<Record<"brochure_first_name" | "brochure_email" | "brochure_base", string>>

type Sent = { token: string; email: string | null }

type GateContextValue = {
  available: boolean
  openGate: (opener: HTMLElement | null) => void
}

const GateContext = React.createContext<GateContextValue>({ available: false, openGate: () => {} })

export function brochureFileUrl(token: string) {
  return `/brochure/file?token=${encodeURIComponent(token)}`
}

// Tokens this tab has already started a download for (module state, touched
// only from effects, so SSR never sees it).
const startedTokens = new Set<string>()

export function BrochureGateProvider({ children }: { children: React.ReactNode }) {
  const { props } = usePage<PageProps>()
  const available = Boolean(props.brochure_available)
  const token = props.flash?.brochure_token ?? null
  const accountEmail = props.current_user?.email ?? null

  const [open, setOpen] = React.useState(false)
  const [sent, setSent] = React.useState<Sent | null>(null)
  const [accountError, setAccountError] = React.useState<string | null>(null)
  const [posting, setPosting] = React.useState(false)
  const openerRef = React.useRef<HTMLElement | null>(null)

  // The download: once per token. The token is first dropped from the page
  // (and so from this history entry), so Back never starts it again, then
  // the file is requested as a plain navigation.
  React.useEffect(() => {
    if (!token || startedTokens.has(token)) return
    startedTokens.add(token)
    router.replace({
      props: (current) => ({ ...current, flash: { ...(current.flash as object), brochure_token: null } }),
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        window.location.href = brochureFileUrl(token)
      },
    })
  }, [token])

  // A signed-in visitor sends nothing: the account's email is used.
  const requestForAccount = React.useCallback(() => {
    setPosting(true)
    setAccountError(null)
    router.post(
      "/brochure",
      {},
      {
        preserveState: true,
        preserveScroll: true,
        onSuccess: (page) => {
          const next = (page.props as unknown as PageProps).flash?.brochure_token
          if (next) setSent({ token: next, email: accountEmail })
          setOpen(true)
        },
        onError: (errors) => {
          const gateErrors = errors as GateErrors
          setAccountError(
            gateErrors.brochure_base ?? gateErrors.brochure_email ?? "Something went wrong. Please try again.",
          )
          setOpen(true)
        },
        onFinish: () => setPosting(false),
      },
    )
  }, [accountEmail])

  const openGate = React.useCallback(
    (opener: HTMLElement | null) => {
      openerRef.current = opener
      setSent(null)
      setAccountError(null)
      if (accountEmail) {
        if (!posting) requestForAccount()
      } else {
        setOpen(true)
      }
    },
    [accountEmail, posting, requestForAccount],
  )

  const value = React.useMemo(() => ({ available, openGate }), [available, openGate])

  return (
    <GateContext.Provider value={value}>
      {children}
      {available && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="studio-modal brochure-modal"
            aria-describedby="brochure-gate-description"
            onCloseAutoFocus={(event) => {
              // Back to the link that opened the gate (Radix only knows its
              // own trigger, and the gate has many).
              event.preventDefault()
              openerRef.current?.focus()
            }}
          >
            {sent ? (
              <SentView sent={sent} />
            ) : accountEmail ? (
              <AccountRetryView
                email={accountEmail}
                error={accountError}
                posting={posting}
                onRetry={requestForAccount}
              />
            ) : (
              <GuestForm onSent={setSent} />
            )}
          </DialogContent>
        </Dialog>
      )}
    </GateContext.Provider>
  )
}

function useBrochureGate() {
  return React.useContext(GateContext)
}

function GuestForm({ onSent }: { onSent: (sent: Sent) => void }) {
  const form = useForm<GateFields>({
    first_name: "",
    email: "",
    marketing_consent: false,
    website: "",
  })
  const errors = form.errors as GateErrors

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const email = form.data.email.trim()
    form.transform((data) => ({ ...data, marketing_consent: data.marketing_consent ? "1" : "0" }))
    form.post("/brochure", {
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        const token = (page.props as unknown as PageProps).flash?.brochure_token
        if (token) {
          onSent({ token, email })
          form.reset()
        }
      },
    })
  }

  return (
    <>
      <DialogHeader>
        <div className="eyebrow">Brochure</div>
        <DialogTitle>Download our brochure</DialogTitle>
        <DialogDescription id="brochure-gate-description">
          The wines, the five steps and bottles we have made, in one PDF. Tell us where to send a copy
          and your download starts straight away.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} noValidate className="studio-gate-form">
        {errors.brochure_base && (
          <div role="alert" className="field-error">
            {errors.brochure_base}
          </div>
        )}

        <div>
          <label htmlFor="brochure-first-name" className="field-label">
            First name
          </label>
          <Input
            id="brochure-first-name"
            autoComplete="given-name"
            required
            value={form.data.first_name}
            onChange={(event) => form.setData("first_name", event.target.value)}
            aria-invalid={Boolean(errors.brochure_first_name)}
            aria-describedby={errors.brochure_first_name ? "brochure-first-name-error" : undefined}
            className="form-control-cellar"
          />
          {errors.brochure_first_name && (
            <div id="brochure-first-name-error" className="field-error">
              {errors.brochure_first_name}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="brochure-email" className="field-label">
            Email
          </label>
          <Input
            id="brochure-email"
            type="email"
            autoComplete="email"
            required
            value={form.data.email}
            onChange={(event) => form.setData("email", event.target.value)}
            aria-invalid={Boolean(errors.brochure_email)}
            aria-describedby={errors.brochure_email ? "brochure-email-error" : undefined}
            className="form-control-cellar"
          />
          {errors.brochure_email && (
            <div id="brochure-email-error" className="field-error">
              {errors.brochure_email}
            </div>
          )}
        </div>

        <div>
          <label htmlFor="brochure-consent" className="studio-consent font-normal text-ink-body">
            <Checkbox
              id="brochure-consent"
              checked={form.data.marketing_consent}
              onChange={(event) => form.setData("marketing_consent", event.target.checked)}
              aria-describedby="brochure-consent-hint"
            />
            <span>Keep me posted about SOH GD Wines — occasional emails, unsubscribe any time</span>
          </label>
          <div id="brochure-consent-hint" className="field-hint">
            The download works whether or not you tick the box. See our{" "}
            <Link href="/privacy">privacy policy</Link>.
          </div>
        </div>

        {/* Honeypot: hidden from people and assistive tech; bots fill it. */}
        <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
          <label htmlFor="brochure-website">Website</label>
          <input
            id="brochure-website"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.data.website}
            onChange={(event) => form.setData("website", event.target.value)}
          />
        </div>

        <Button type="submit" variant="gold" size="lg" disabled={form.processing}>
          {form.processing ? "Preparing…" : "Download the brochure"}
        </Button>
      </form>
    </>
  )
}

function AccountRetryView({
  email,
  error,
  posting,
  onRetry,
}: {
  email: string
  error: string | null
  posting: boolean
  onRetry: () => void
}) {
  return (
    <>
      <DialogHeader>
        <div className="eyebrow">Brochure</div>
        <DialogTitle>Download our brochure</DialogTitle>
        <DialogDescription id="brochure-gate-description">
          We will send a copy to {email} as your download starts.
        </DialogDescription>
      </DialogHeader>
      <div className="studio-gate-form">
        {error && (
          <div role="alert" className="field-error">
            {error}
          </div>
        )}
        <Button type="button" variant="gold" size="lg" disabled={posting} onClick={onRetry}>
          {posting ? "Preparing…" : "Download the brochure"}
        </Button>
      </div>
    </>
  )
}

function SentView({ sent }: { sent: Sent }) {
  return (
    <>
      <DialogHeader>
        <div className="eyebrow">Brochure</div>
        <DialogTitle>Your brochure is downloading</DialogTitle>
        <DialogDescription id="brochure-gate-description">
          {sent.email ? (
            <>
              A copy is on its way to <span className="brochure-email">{sent.email}</span>.
            </>
          ) : (
            "A copy is on its way to your inbox."
          )}
        </DialogDescription>
      </DialogHeader>
      <div className="studio-gate-form">
        <p className="brochure-fallback">
          Download not started?{" "}
          {/* A plain link: the file is a raw PDF response, not an Inertia page. */}
          <a href={brochureFileUrl(sent.token)}>Download the brochure</a>
        </p>
        <div className="brochure-next">
          <div className="field-label">Ready to make it real?</div>
          <Button asChild variant="gold" size="lg">
            <Link href="/contact">Request a Consultation</Link>
          </Button>
        </div>
      </div>
    </>
  )
}

/** "Download our brochure" as a public button (ghost by default); hidden without a brochure. */
export function BrochureButton({
  variant = "ghost-hairline",
  size = "lg",
  className,
  children = "Download our brochure",
}: {
  variant?: ButtonProps["variant"]
  size?: ButtonProps["size"]
  className?: string
  children?: React.ReactNode
}) {
  const { available, openGate } = useBrochureGate()
  if (!available) return null

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={className}
      aria-haspopup="dialog"
      onClick={(event) => openGate(event.currentTarget)}
    >
      {children}
    </Button>
  )
}

/** "Download our brochure" as a text link (the footer); hidden without a brochure. */
export function BrochureLink({
  className,
  children = "Download our brochure",
}: {
  className?: string
  children?: React.ReactNode
}) {
  const { available, openGate } = useBrochureGate()
  if (!available) return null

  return (
    <button
      type="button"
      className={cn("brochure-link", className)}
      aria-haspopup="dialog"
      onClick={(event) => openGate(event.currentTarget)}
    >
      {children}
    </button>
  )
}

/** True when the brochure links show (a PDF is attached). */
export function useBrochureAvailable() {
  return useBrochureGate().available
}
